import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { LEGAL_DOCS, LEGAL_ORDER, LEGAL_META } from "@/content/legalDocs";

const LEGAL_STYLES = `
.legal-prose { color:#3a3f4b; line-height:1.7; font-size:0.95rem; }
.legal-prose h1.doc-title { font-size:1.9rem; font-weight:800; color:#1a1d29; margin:0 0 .25rem; line-height:1.2; }
.legal-prose h2 { font-size:1.15rem; font-weight:700; color:#1a1d29; margin:2rem 0 .5rem; }
.legal-prose h3 { font-size:1rem; font-weight:700; color:#1a1d29; margin:1.25rem 0 .4rem; }
.legal-prose h4 { font-size:.95rem; font-weight:600; color:#1a1d29; margin:1rem 0 .3rem; }
.legal-prose p { margin:0 0 .8rem; }
.legal-prose ul, .legal-prose ol { margin:.4rem 0 1rem; padding-left:1.4rem; }
.legal-prose li { margin:.25rem 0; }
.legal-prose a { color:#202123; text-decoration:underline; }
.legal-prose strong { color:#1a1d29; }
.legal-prose table.data-table { width:100%; border-collapse:collapse; margin:1rem 0 1.5rem; font-size:.88rem; }
.legal-prose table.data-table th, .legal-prose table.data-table td { border:1px solid #e2e5ea; padding:.55rem .7rem; text-align:left; vertical-align:top; }
.legal-prose table.data-table th { background:#f5f6f8; font-weight:600; color:#1a1d29; }
.legal-prose table.summary-box { width:100%; border-collapse:collapse; margin:0 0 1.75rem; }
.legal-prose table.summary-box td { background:#f4f6ff; border:1px solid #c9d2f0; border-left:4px solid #202123; padding:1rem 1.15rem; }
.legal-prose table.summary-box p { margin:.35rem 0; font-size:.9rem; }
`;

export default function Legal({ doc }) {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeLang = (lang || "en").toLowerCase() === "fr" ? "fr" : "en";
  const content = LEGAL_DOCS[doc] || LEGAL_DOCS.terms;
  const pfx = activeLang === "fr" ? "/fr" : "";

  const others = LEGAL_ORDER.filter((s) => s !== doc);

  return (
    <div className="min-h-screen bg-background text-navy" data-testid={`legal-${doc}`}>
      <style>{LEGAL_STYLES}</style>
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="legal-logo">
            <img src="/jotbay-logo.png" alt="JotBay" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-1" data-testid="legal-lang-toggle">
            <button onClick={() => navigate(`/${doc}`)} data-testid="legal-lang-en"
              className={`text-xs font-medium rounded-md px-2.5 py-1 transition-colors ${activeLang === "en" ? "bg-[#ECECEC] text-[#202123] border border-[#C5C5D0]" : "text-slatebody hover:bg-slate-100"}`}>EN</button>
            <button onClick={() => navigate(`/fr/${doc}`)} data-testid="legal-lang-fr"
              className={`text-xs font-medium rounded-md px-2.5 py-1 transition-colors ${activeLang === "fr" ? "bg-[#ECECEC] text-[#202123] border border-[#C5C5D0]" : "text-slatebody hover:bg-slate-100"}`}>FR</button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#202123] font-medium hover:underline mb-6" data-testid="legal-back">
          <ArrowLeft className="h-4 w-4" /> {t("legal.back")}
        </Link>

        {activeLang === "fr" && (
          <div className="rounded-lg border border-slate-200 bg-accent/40 px-4 py-3 mb-6 text-sm text-navy" data-testid="legal-fr-notice">
            {t("legal.frComingSoon")}
          </div>
        )}

        <article className="legal-prose" data-testid="legal-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.html) }} />

        <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slatebody" data-testid="legal-see-also">
          <span className="font-medium text-navy">{t("legal.seeAlso")}:</span>{" "}
          {others.map((s, i) => (
            <span key={s}>
              <Link to={`${pfx}/${s}`} className="text-[#202123] font-medium hover:underline" data-testid={`legal-see-${s}`}>{LEGAL_DOCS[s].title}</Link>
              {i < others.length - 1 ? " · " : ""}
            </span>
          ))}
          <p className="text-xs mt-4" data-testid="legal-copyright">© 2026 {LEGAL_META.entity}</p>
        </div>
      </main>
    </div>
  );
}
